// The refresh token the portal hands auth0-spa-js comes from IndexedDB, not localStorage (AB#5741).
//
// With MRRT one rotating refresh token serves every API, and every tab of the portal exchanges it, one
// exchange at a time under the portal's token lock (auth/tokenRequestLock). localStorage cannot hand the
// token from one exchange to the next. Another tab's writes reach this tab's copy of localStorage
// asynchronously, and not in order with the lock. Under load, a tab that has just been granted the lock can
// still read a token the previous holder already rotated: E2E run 9098 read one two rotations old, three
// seconds after it was replaced. Auth0 then refuses it and revokes the whole token family. IndexedDB goes
// through the browser's one database in order, so a tab that takes the lock reads what the previous holder
// committed before releasing it.
//
// The store keeps one token per client id and only moves forward. auth0-spa-js writes back the token it
// read when it rewrites an expired entry. A token the store has replaced, or cleared at sign-out, is never
// put back, so such a write-back from a tab that read before another tab rotated changes nothing. Replaced
// tokens are remembered only by a SHA-256 fingerprint.

const DATABASE_NAME = 'zespy-portal-auth';
const DATABASE_VERSION = 1;
const STORE_NAME = 'refresh-tokens';
// More rotations than any tab can fall behind by; the oldest fingerprints are dropped.
const REPLACED_FINGERPRINT_LIMIT = 64;

export interface RefreshTokenStore {
  /** The current refresh token for the client, if any. */
  read(clientId: string): Promise<string | undefined>;
  /** Makes `token` current, unless it is current already or was replaced or cleared before. */
  offer(clientId: string, token: string): Promise<void>;
  /** Removes the current token (sign-out); it is remembered as replaced. */
  clear(clientId: string): Promise<void>;
}

interface StoredRefreshToken {
  token?: string;
  fingerprint?: string;
  replaced: string[];
}

const fingerprintOf = async (token: string) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const retire = (record: StoredRefreshToken | undefined) => (record?.fingerprint
  ? [record.fingerprint, ...record.replaced]
  : [...(record?.replaced ?? [])]).slice(0, REPLACED_FINGERPRINT_LIMIT);

const openDatabase = (factory: IDBFactory, name: string) => new Promise<IDBDatabase>((resolve, reject) => {
  const request = factory.open(name, DATABASE_VERSION);
  request.onupgradeneeded = () => {
    if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME);
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error ?? new Error('The refresh-token database could not be opened.'));
});

const isClosedConnection = (error: unknown) => error instanceof DOMException && error.name === 'InvalidStateError';

/**
 * The refresh-token store on IndexedDB. Each read and each change is one transaction, so a change resolves
 * only once it is committed: a tab that then releases the token lock hands over a token the next holder reads.
 */
export const createIndexedDbRefreshTokenStore = (
  factory: () => IDBFactory = () => indexedDB,
  databaseName = DATABASE_NAME,
): RefreshTokenStore => {
  let database: Promise<IDBDatabase> | undefined;
  let current: IDBDatabase | undefined;
  const forget = (connection: IDBDatabase) => {
    connection.close();
    if (current !== connection) return;
    current = undefined;
    database = undefined;
  };
  // One connection per tab, opened again whenever it is lost: another tab upgrading the schema (which waits
  // for every connection to close), the database being deleted, or the browser closing it.
  const connect = () => {
    database ??= openDatabase(factory(), databaseName).then((connection) => {
      current = connection;
      connection.onversionchange = () => forget(connection);
      connection.onclose = () => forget(connection);
      return connection;
    }, (error: unknown) => {
      // Try again next time: an unavailable database need not stay unavailable.
      database = undefined;
      throw error;
    });
    return database;
  };

  // Runs `work` in one transaction and resolves with what it returns once the transaction has committed.
  const transact = async <T,>(mode: IDBTransactionMode, work: (store: IDBObjectStore, done: (value: T) => void) => void): Promise<T> => {
    let connection = await connect();
    let transaction: IDBTransaction;
    try {
      transaction = connection.transaction(STORE_NAME, mode);
    } catch (error) {
      // A connection closed without telling this tab: open a new one, once.
      if (!isClosedConnection(error)) throw error;
      forget(connection);
      connection = await connect();
      transaction = connection.transaction(STORE_NAME, mode);
    }
    return new Promise<T>((resolve, reject) => {
      let result: T;
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error ?? new Error('A refresh-token transaction failed.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('A refresh-token transaction was aborted.'));
      work(transaction.objectStore(STORE_NAME), (value) => { result = value; });
    });
  };

  // Reads the client's record and, in the same transaction, writes what `next` makes of it (if anything).
  const update = (clientId: string, next: (record: StoredRefreshToken | undefined) => StoredRefreshToken | undefined) =>
    transact<void>('readwrite', (store) => {
      const request = store.get(clientId);
      request.onsuccess = () => {
        const record = next(request.result as StoredRefreshToken | undefined);
        if (record) store.put(record, clientId);
      };
    });

  return {
    read: (clientId) => transact<string | undefined>('readonly', (store, done) => {
      const request = store.get(clientId);
      request.onsuccess = () => done((request.result as StoredRefreshToken | undefined)?.token);
    }),
    offer: async (clientId, token) => {
      // Hashed before the transaction opens: a transaction ends when it has no request pending.
      const fingerprint = await fingerprintOf(token);
      await update(clientId, (record) => {
        if (record?.token === token || record?.replaced.includes(fingerprint)) return undefined;
        return { token, fingerprint, replaced: retire(record) };
      });
    },
    clear: (clientId) => update(clientId, (record) => (record?.token ? { replaced: retire(record) } : undefined)),
  };
};
